import PropTypes from 'prop-types';
import QuestionsAndAnswersForm from './QuestionsAndAnswersForm';
import QuestionsAndAnswersList from './QuestionsAndAnswersList';
import React, { useCallback, useEffect, useState } from 'react';
import '../styles/QuestionsAndAnswers.css';
import PageNavButtons from './PageNavButtons';


/** CONFIGURATION */
// The default number of questions to show in the component.
const DEFAULT_LIMIT = 10;

/**
 * Display a list of questions and answers for a product and a form for
 * submitting questions to the administrators. Approval of questions are not
 * implemented as of the moment but is recommended to filter irrelevant
 * questions from the page.
 * @component
 */
const QuestionsAndAnswers = ({ productId }) => {
    /** States */
    // The current page to show in a paginated list of questions.
    const [currentPage, setCurrentPage] = useState(0);
    // The current limit to the number of questions to show.
    const [currentLimit, setCurrentLimit] = useState(DEFAULT_LIMIT);
    // The list of questions to show.
    const [questions, setQuestions] = useState([]);
    // Indicate whether the questions have been loaded or not.
    const [questionsLoaded, setQuestionsLoaded] = useState(false);
    // All page related data
    const [questionPagesData, setQuestionPagesData] = useState({});

    const onQuestionSubmit = () => {
        fetchQuestions();
    };

    /**
     * Get product questions from the API.
     */
    const fetchQuestions = useCallback(async () => {
        try {
            setQuestionsLoaded(false);
            const url = new URL('/api/questions', process.env.APP_URL);
            const params = new URLSearchParams({
                page: currentPage ?? 0,
                limit: currentLimit ?? DEFAULT_LIMIT,
                productId,
            });
            const response = await fetch(`${url}?${params}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            if (!response.ok)
                throw new Error('Something went wrong.');
            const data = await response.json();

            const limit = data.pageInfo.limitPerPage;
            const count = data.pageInfo.totalObjects;
            setQuestionPagesData(data.pageInfo ? {
                limit,
                startIndex: limit * currentPage + 1,
                endIndex: Math.min(limit * (currentPage + 1), count),
                count,
                pages: data.pageInfo?.totalPages,
            } : {});
            setQuestions(data.questions);
            setQuestionsLoaded(true);
        } catch (e) {
            console.error(e);
        }
    }, [currentLimit, currentPage, productId]);

    useEffect(() => {
        fetchQuestions();
    }, [currentPage]);

    /** UI */
    return <section
        className={'qanda--container'}>
        <h2>Questions & Answers</h2>
        <hr />
        <QuestionsAndAnswersForm
            productId={productId}
            onQuestionSubmit={onQuestionSubmit}
        />
        <div
            className={'mb-3'}>
            {questionsLoaded
                ? <QuestionsAndAnswersList
                    questions={questions} />
                : <p>Fetching questions...</p>
            }
            {
                questionsLoaded && questions.length > 0 && <>
                    <PageNavButtons
                        curPage={currentPage}
                        totalPages={questionPagesData.pages}
                        paginateUser={setCurrentPage}
                    />
                </>
            }
        </div>
    </section>;
};

/** Prop Types */
QuestionsAndAnswers.propTypes = {
    /** This is the ID of the product to fetch questions from. */
    productId: PropTypes.number,
};

export default QuestionsAndAnswers;
